<template>
    <div>
        <ts-page-title
            :title="$t('cashRequest.pageTitle')"
            :breadcrumb="[
                { text: $t('home'), href: '/' },
                { text: $t('cashRequest.pageTitle'), active: true }
            ]"
        />
        <ts-panel>
            <ts-panel-wrapper
                class="tw-flex tw-items-center tw-justify-between"
            >
                <Button
                    type="info"
                    ghost
                    @click="$router.push({ name: 'cash-request-create' })"
                >
                    {{ $t("addNew") }}
                </Button>
                <div class="tw-flex tw-justify-end tw-space-x-2">
                    <Input
                        search
                        v-model="search"
                        :placeholder="$t('cashRequest.search')"
                        :style="width"
                        @on-focus="() => (width = 'width: 500px')"
                        @on-blur="() => (width = 'width: 300px')"
                    />
                    <ts-button
                        v-tooltip="$t('filter')"
                        @click.prevent="visible = true"
                    >
                        <i class="fa fa-filter"></i>
                    </ts-button>
                </div>
            </ts-panel-wrapper>

            <Table
                :columns="columns"
                :data="resources"
                size="small"
                :loading="loading"
                max-height="700"
                highlight-row
                stripe
            >
                <template slot-scope="{ row }" slot="request_no">
                    <span class="tw-font-bold text-primary">{{ row.request_no }}</span>
                </template>

                <template slot-scope="{ row }" slot="branch">
                    {{ row.branch ? row.branch.branch_name_en : "" }}
                </template>

                <template slot-scope="{ row }" slot="requested_by">
                    <span class="ts-font-bold tw-whitespace-no-wrap text-blue">
                        <i class="fas fa-user-tie"></i>
                        {{ row.requested_by ? row.requested_by.employee_name_en : "" }}
                    </span>
                    <br />
                    <span class="tw-text-xs tw-text-gray-500">
                        <i class="fas fa-id-card"></i>
                        {{ row.requested_by ? row.requested_by.card_id : "" }}
                    </span>
                </template>

                <template slot-scope="{ row }" slot="amount">
                    <span class="tw-font-bold">
                        {{
                            $store.getters.formatCurrencyWithCode(
                                row.request_amount,
                                row.currency
                            )
                        }}
                    </span>
                </template>

                <template slot-scope="{ row }" slot="bank_slip">
                    <i
                        v-if="row.bank_slip_photo"
                        class="fas fa-check-circle text-success"
                        v-tooltip="row.bank_slip_date"
                    ></i>
                    <i
                        v-else
                        class="fas fa-times-circle text-danger"
                    ></i>
                </template>

                <template slot-scope="{ row }" slot="approval_status">
                    <Poptip
                        trigger="hover"
                        title="Approvers"
                        theme="light"
                        transfer
                        v-if="
                            row.approval_matrix &&
                                row.approval_matrix.length > 0 &&
                                row.approval_status < 2
                        "
                    >
                        <template #content>
                            <ul
                                v-for="(level, idx) in approvalLevel(
                                    row.approval_matrix
                                )"
                                :key="idx"
                            >
                                <b>{{ "Level: " + idx }}</b>
                                <li
                                    v-for="(u, i) in level"
                                    :key="i"
                                    class="tw-px-2 tw-whitespace-no-wrap"
                                >
                                    - {{ u.employee ? u.employee.employee_name_en : u.app_user_id }}
                                </li>
                            </ul>
                        </template>
                        <span :class="statusStyle(row.approval_status)">
                            {{ statusLabel(row.approval_status) }}
                            <span v-if="row.approval_status < 2">
                                Lv:{{ row.current_app_level }}
                            </span>
                        </span>
                    </Poptip>
                    <span v-else :class="statusStyle(row.approval_status)">
                        {{ statusLabel(row.approval_status) }}
                    </span>
                </template>

                <template slot-scope="{ row }" slot="action">
                    <!-- Edit: only requester, only pending -->
                    <a
                        v-if="row.approval_status === 0 && row.requested_by && row.requested_by.employee_id === myEmployeeId"
                        href="#"
                        class="text-primary ml-2"
                        @click.prevent="onEdit(row)"
                        v-tooltip="$t('edit')"
                    >
                        <Icon type="ios-create" size="20" />
                    </a>

                    <!-- Reject -->
                    <a
                        v-if="canApprove(row)"
                        href="#"
                        class="text-warning ml-2"
                        @click.prevent="onApprovalModal(row, 3)"
                        v-tooltip="$t('cashRequest.reject')"
                    >
                        <Icon type="ios-close-circle" size="20" />
                    </a>

                    <!-- Approve -->
                    <a
                        v-if="canApprove(row)"
                        href="#"
                        class="text-info ml-2"
                        @click.prevent="onApprovalModal(row, 2)"
                        v-tooltip="$t('cashRequest.approve')"
                    >
                        <Icon type="ios-checkmark-circle" size="20" />
                    </a>

                    <!-- Bank Slip -->
                    <a
                        v-if="row.approval_status === 2"
                        href="#"
                        class="text-success ml-2"
                        @click.prevent="onBankSlipModal(row)"
                        v-tooltip="$t('cashRequest.bankSlip')"
                    >
                        <Icon type="ios-paper" size="20" />
                    </a>

                    <!-- Delete: only requester, only pending -->
                    <Poptip
                        v-if="row.approval_status === 0 && row.requested_by && row.requested_by.employee_id === myEmployeeId"
                        :title="$t('are_you_sure_to_delete')"
                        :transfer="true"
                        width="200"
                    >
                        <a
                            class="ml-2 text-danger"
                            v-tooltip="$t('delete')"
                            :disabled="row._deleting"
                        >
                            <i
                                class="fas fa-circle-notch fa-spin"
                                v-if="row._deleting"
                            ></i>
                            <Icon type="ios-trash" size="20" v-else />
                        </a>
                        <div slot="content" class="tw-text-right tw-mt-2">
                            <a
                                class="ivu-btn ivu-btn-danger ivu-btn-small"
                                @click.prevent="onDelete(row)"
                            >
                                <span>{{ $t("ok") }}</span>
                            </a>
                        </div>
                    </Poptip>
                </template>
            </Table>

            <div class="tw-flex tw-items-center tw-justify-end tw-p-4">
                <ts-i-pagination
                    v-model="pagination"
                    @navigate="fetchData"
                ></ts-i-pagination>
            </div>
        </ts-panel>

        <!-- Filter Drawer -->
        <Drawer
            :title="$t('filter')"
            :closable="false"
            :value="visible"
            @on-close="() => (visible = false)"
            width="300px"
        >
            <div class="mb-3">
                <label class="form-col-label control-label">{{
                    $t("branchName")
                }}</label>
                <ts-branch-filter
                    @filter="value => (filter.branch_id = value)"
                />
            </div>
            <div class="mb-3">
                <label class="form-col-label control-label">{{
                    $t("cashRequest.status")
                }}</label>
                <Select
                    v-model="filter.approval_status"
                    multiple
                    clearable
                    :placeholder="$t('all')"
                >
                    <Option :value="0">{{ $t("cashRequest.pending") }}</Option>
                    <Option :value="1">{{ $t("cashRequest.partiallyApproved") }}</Option>
                    <Option :value="2">{{ $t("cashRequest.approved") }}</Option>
                    <Option :value="3">{{ $t("cashRequest.rejected") }}</Option>
                </Select>
            </div>
        </Drawer>

        <!-- Approve / Reject Modal -->
        <Modal
            v-model="approvalModal"
            width="320"
            @on-cancel="onApprovalCancel"
            :loading="approving"
        >
            <p slot="header" style="color:#f60; text-align:center">
                <Icon type="ios-information-circle" />
                <span>{{
                    approval.approve_status_id === 2
                        ? $t("cashRequest.areYouSureApprove")
                        : $t("cashRequest.areYouSureReject")
                }}</span>
            </p>
            <div>
                <label :class="{ required: approval.approve_status_id === 3 }">
                    {{ $t("cashRequest.reason") }}
                </label>
                <textarea
                    v-model.trim="approval.approval_comment"
                    class="form-control"
                    :class="{ 'is-invalid': errors.has('approval_comment') }"
                    rows="3"
                ></textarea>
                <div
                    class="invalid-feedback"
                    v-if="errors.has('approval_comment')"
                >
                    {{ errors.first("approval_comment") }}
                </div>
            </div>
            <div slot="footer">
                <Button
                    :type="approval.approve_status_id === 2 ? 'info' : 'warning'"
                    size="large"
                    long
                    @click="onApproval"
                    :loading="approving"
                >
                    {{ $t("yes") }}
                </Button>
            </div>
        </Modal>

        <!-- Bank Slip Modal -->
        <Modal
            v-model="bankSlipModal"
            width="420"
            :title="$t('cashRequest.bankSlip')"
            @on-cancel="onBankSlipCancel"
        >
            <div class="row">
                <div class="col-md-12 tw-mb-3">
                    <label class="required form-col-label control-label">
                        {{ $t("cashRequest.bankSlipPhoto") }}
                    </label>
                    <div class="tw-flex tw-items-center tw-space-x-2">
                        <input
                            type="file"
                            accept="image/*"
                            ref="slipFileInput"
                            @change="onSlipFileChange"
                            class="form-control"
                        />
                        <i
                            class="fas fa-circle-notch fa-spin text-primary"
                            v-if="uploadingPhoto"
                        ></i>
                    </div>
                    <small v-if="bankSlip.bank_slip_photo" class="text-success">
                        <i class="fas fa-check"></i>
                        {{ bankSlip.bank_slip_photo.split("/").pop() }}
                    </small>
                    <div
                        class="invalid-feedback d-block"
                        v-if="errors.has('bank_slip_photo')"
                    >
                        {{ errors.first("bank_slip_photo") }}
                    </div>
                </div>

                <div class="col-md-12 tw-mb-3">
                    <label class="required form-col-label control-label">
                        {{ $t("cashRequest.bankSlipDate") }}
                    </label>
                    <DatePicker
                        v-model="bankSlip.bank_slip_date"
                        type="date"
                        format="dd-MM-yyyy"
                        style="width: 100%"
                        :class="{ 'is-invalid': errors.has('bank_slip_date') }"
                    />
                    <div
                        class="invalid-feedback"
                        v-if="errors.has('bank_slip_date')"
                    >
                        {{ errors.first("bank_slip_date") }}
                    </div>
                </div>

                <div class="col-md-12 tw-mb-3">
                    <label class="form-col-label control-label">
                        {{ $t("cashRequest.bankSlipRemark") }}
                    </label>
                    <textarea
                        v-model.trim="bankSlip.bank_slip_remark"
                        class="form-control"
                        rows="2"
                    ></textarea>
                </div>
            </div>
            <div slot="footer">
                <Button @click="onBankSlipCancel">{{ $t("cancel") }}</Button>
                <Button
                    type="primary"
                    :loading="savingBankSlip"
                    @click="onSaveBankSlip"
                >
                    {{ $t("save") }}
                </Button>
            </div>
        </Modal>
    </div>
</template>

<script>
import { mapState } from "vuex";
import { debounce, groupBy } from "lodash";
import { Errors } from "form-backend-validation";
import moment from "moment";

export default {
    name: "CashRequestIndex",
    data() {
        return {
            loading: false,
            errors: new Errors(),
            width: "width: 300px",
            visible: false,
            // approval
            approvalModal: false,
            approving: false,
            approval: {
                request_id: null,
                approve_status_id: null,
                approval_comment: ""
            },
            // bank slip
            bankSlipModal: false,
            savingBankSlip: false,
            uploadingPhoto: false,
            bankSlip: {
                request_id: null,
                bank_slip_photo: null,
                bank_slip_date: moment(new Date()).format("DD-MM-YYYY"),
                bank_slip_remark: ""
            },
            // filters
            filter: {
                branch_id: [],
                approval_status: []
            }
        };
    },
    computed: {
        ...mapState("finance/cashRequest", ["resources", "pagination"]),
        user_id() {
            return this.$store.state.authUser.user.user_id;
        },
        myEmployeeId() {
            return this.$store.state.authUser.user.employee_id;
        },
        search: {
            get() {
                return this.$store.state.finance.cashRequest.search;
            },
            set(value) {
                this.$store.commit("finance/cashRequest/SET_SEARCH", value);
                this.$store.commit("finance/cashRequest/RESET_CURRENT_PAGE");
            }
        },
        columns() {
            return [
                {
                    title: this.$t("cashRequest.requestNo"),
                    slot: "request_no",
                    fixed: "left",
                    minWidth: 170
                },
                {
                    title: this.$t("cashRequest.branch"),
                    slot: "branch",
                    minWidth: 130
                },
                {
                    title: this.$t("cashRequest.requestedBy"),
                    slot: "requested_by",
                    minWidth: 200,
                    fixed: "left"
                },
                {
                    title: this.$t("cashRequest.requestDate"),
                    key: "request_date",
                    minWidth: 120,
                    align: "center",
                    render: (h, { row }) =>
                        h("span", row.request_date ? moment(row.request_date).format("DD-MM-YYYY") : "")
                },
                {
                    title: this.$t("cashRequest.amount"),
                    slot: "amount",
                    minWidth: 150,
                    align: "right"
                },
                {
                    title: this.$t("cashRequest.purpose"),
                    key: "purpose",
                    minWidth: 280
                },
                {
                    title: this.$t("cashRequest.bankSlip"),
                    slot: "bank_slip",
                    minWidth: 90,
                    align: "center"
                },
                {
                    title: this.$t("cashRequest.status"),
                    slot: "approval_status",
                    minWidth: 150,
                    fixed: "right",
                    align: "center"
                },
                {
                    title: this.$t("actions"),
                    slot: "action",
                    fixed: "right",
                    align: "center",
                    width: 140
                }
            ];
        }
    },
    methods: {
        fetchData(attributes = {}) {
            this.loading = true;
            this.$store
                .dispatch("finance/cashRequest/fetch", {
                    ...attributes,
                    filter: {
                        search: this.search,
                        branch_id: this.filter.branch_id,
                        approval_status: this.filter.approval_status
                    }
                })
                .finally(() => (this.loading = false));
        },
        onEdit(row) {
            this.$store.commit(
                "finance/cashRequest/SET_EDIT_DATA",
                row
            );
            this.$router.push({
                name: "cash-request-edit",
                params: { id: row.request_id }
            });
        },
        onDelete(row) {
            row._deleting = true;
            this.$store
                .dispatch("finance/cashRequest/destroy", row.request_id)
                .then(response => {
                    this.notice({
                        type: response.message_id == 0 ? "success" : "warning",
                        text: response.message
                    });
                    this.fetchData();
                })
                .catch(error =>
                    this.notice({ type: "error", text: error.message })
                )
                .finally(() => (row._deleting = false));
        },
        canApprove(row) {
            if (row.approval_status > 1) return false;
            if (!row.approval_matrix || row.approval_matrix.length === 0)
                return false;
            return (
                row.approval_matrix.some(
                    m => m.app_user_id === this.user_id
                ) &&
                row.approval_matrix.some(
                    m => m.app_level === row.current_app_level
                )
            );
        },
        onApprovalModal(row, statusId) {
            this.approval.request_id = row.request_id;
            this.approval.approve_status_id = statusId;
            this.approval.approval_comment = "";
            this.errors = new Errors();
            this.approvalModal = true;
        },
        onApprovalCancel() {
            this.approvalModal = false;
        },
        onApproval() {
            this.approving = true;
            this.errors = new Errors();
            this.$store
                .dispatch("finance/cashRequest/approveOrReject", {
                    id: this.approval.request_id,
                    data: this.approval
                })
                .then(response => {
                    this.notice({
                        type: response.message_id == 0 ? "success" : "warning",
                        text: response.message
                    });
                    this.approvalModal = false;
                    this.fetchData();
                })
                .catch(error => {
                    this.errors = new Errors(error.errors);
                    this.notice({ type: "error", text: error.message });
                })
                .finally(() => (this.approving = false));
        },
        onBankSlipModal(row) {
            this.bankSlip.request_id = row.request_id;
            this.bankSlip.bank_slip_photo = row.bank_slip_photo || null;
            this.bankSlip.bank_slip_date = row.bank_slip_date
                ? moment(row.bank_slip_date).format("DD-MM-YYYY")
                : moment(new Date()).format("DD-MM-YYYY");
            this.bankSlip.bank_slip_remark = row.bank_slip_remark || "";
            this.errors = new Errors();
            this.bankSlipModal = true;
        },
        onBankSlipCancel() {
            this.bankSlipModal = false;
            this.bankSlip.bank_slip_photo = null;
            if (this.$refs.slipFileInput)
                this.$refs.slipFileInput.value = "";
        },
        onSlipFileChange(event) {
            const file = event.target.files[0];
            if (!file) return;
            const formData = new FormData();
            formData.append("photo", file);
            this.uploadingPhoto = true;
            this.$store
                .dispatch("finance/cashRequest/photoUpload", formData)
                .then(path => (this.bankSlip.bank_slip_photo = path))
                .catch(error =>
                    this.notice({ type: "error", text: error.message })
                )
                .finally(() => (this.uploadingPhoto = false));
        },
        onSaveBankSlip() {
            this.savingBankSlip = true;
            this.errors = new Errors();
            this.$store
                .dispatch("finance/cashRequest/bankSlip", {
                    id: this.bankSlip.request_id,
                    data: this.bankSlip
                })
                .then(response => {
                    this.notice({
                        type: response.message_id == 0 ? "success" : "warning",
                        text: response.message
                    });
                    this.bankSlipModal = false;
                    this.fetchData();
                })
                .catch(error => {
                    this.errors = new Errors(error.errors);
                    this.notice({ type: "error", text: error.message });
                })
                .finally(() => (this.savingBankSlip = false));
        },
        approvalLevel(matrix) {
            return groupBy(matrix, "app_level");
        },
        statusLabel(status) {
            const map = {
                0: "Pending",
                1: "Partially Approved",
                2: "Approved",
                3: "Rejected"
            };
            return map[status] || status;
        },
        statusStyle(status) {
            const map = {
                0: "badge bg-warning rounded-pill",
                1: "badge bg-info rounded-pill",
                2: "badge bg-success rounded-pill",
                3: "badge bg-danger rounded-pill"
            };
            return map[status] || "badge bg-secondary rounded-pill";
        },
        notice(not) {
            this.$Notice[not.type]({
                title: "CASH REQUEST",
                desc: not.text
            });
        }
    },
    watch: {
        search: debounce(function() {
            this.fetchData();
        }, 500),
        "filter.branch_id"() {
            this.fetchData();
        },
        "filter.approval_status"() {
            this.fetchData();
        }
    },
    beforeRouteEnter(to, from, next) {
        next(vm => vm.fetchData());
    }
};
</script>
